/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Shared Status Configuration Constants
 * 
 * R5: Single source of truth for status display configuration.
 * Provides consistent styling for status badges, cards, and banners
 * across all VPS UI components.
 */

import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Square,
  AlertCircle,
  type LucideIcon,
} from 'lucide-react';

// ============================================================================
// Status Configuration Types
// ============================================================================

export interface StatusConfig {
  /** Lucide icon component */
  icon: LucideIcon;
  /** Display label */
  label: string;
  /** CSS classes for card variant (badge in list items) */
  cardClassName: string;
  /** CSS classes for banner variant (status banner in detail view) */
  bannerClassName: string;
}

export interface SimpleStatusConfig {
  /** Lucide icon component */
  icon: LucideIcon;
  /** CSS classes for the icon */
  className: string;
}

export interface FullStatusConfig extends SimpleStatusConfig {
  /** Display label */
  label: string;
  /** CSS classes for banner variant */
  bannerClassName: string;
}

// ============================================================================
// Mining Job Status Configuration
// ============================================================================

export type MiningJobStatus = 
  | 'pending'
  | 'running'
  | 'scanning'
  | 'extracting'
  | 'classifying'
  | 'chunking'
  | 'generating'
  | 'review'
  | 'completed'
  | 'failed'
  | 'cancelled';

export const MINING_JOB_STATUS_CONFIG: Record<MiningJobStatus, StatusConfig> = {
  pending: {
    icon: Clock,
    label: 'Pending',
    cardClassName: 'bg-gray-100 text-gray-800 border-gray-200',
    bannerClassName: 'bg-gray-50 text-gray-800',
  },
  running: {
    icon: Loader2,
    label: 'Running',
    cardClassName: 'bg-blue-100 text-blue-800 border-blue-200',
    bannerClassName: 'bg-blue-50 text-blue-800',
  },
  scanning: {
    icon: Loader2,
    label: 'Scanning',
    cardClassName: 'bg-blue-100 text-blue-800 border-blue-200',
    bannerClassName: 'bg-blue-50 text-blue-800',
  },
  extracting: {
    icon: Loader2,
    label: 'Extracting',
    cardClassName: 'bg-blue-100 text-blue-800 border-blue-200',
    bannerClassName: 'bg-blue-50 text-blue-800',
  },
  classifying: {
    icon: Loader2,
    label: 'Classifying',
    cardClassName: 'bg-blue-100 text-blue-800 border-blue-200',
    bannerClassName: 'bg-blue-50 text-blue-800',
  },
  chunking: {
    icon: Loader2,
    label: 'Chunking',
    cardClassName: 'bg-blue-100 text-blue-800 border-blue-200',
    bannerClassName: 'bg-blue-50 text-blue-800',
  },
  generating: {
    icon: Loader2,
    label: 'Generating',
    cardClassName: 'bg-blue-100 text-blue-800 border-blue-200',
    bannerClassName: 'bg-blue-50 text-blue-800',
  },
  review: {
    icon: Clock,
    label: 'Review',
    cardClassName: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    bannerClassName: 'bg-yellow-50 text-yellow-800',
  },
  completed: {
    icon: CheckCircle,
    label: 'Completed',
    cardClassName: 'bg-green-100 text-green-800 border-green-200',
    bannerClassName: 'bg-green-50 text-green-800',
  },
  failed: {
    icon: XCircle,
    label: 'Failed',
    cardClassName: 'bg-red-100 text-red-800 border-red-200',
    bannerClassName: 'bg-red-50 text-red-800',
  },
  cancelled: {
    icon: Square,
    label: 'Cancelled',
    cardClassName: 'bg-gray-100 text-gray-800 border-gray-200',
    bannerClassName: 'bg-gray-50 text-gray-800',
  },
} as const;

// ============================================================================
// MCP Server Status Configuration
// ============================================================================

export type McpServerStatus = 'active' | 'deprecated' | 'unavailable' | 'pending';

export const MCP_SERVER_STATUS_CONFIG: Record<McpServerStatus, FullStatusConfig> = {
  active: {
    icon: CheckCircle,
    label: 'Active',
    className: 'text-green-500',
    bannerClassName: 'text-green-500 bg-green-50',
  },
  deprecated: {
    icon: AlertCircle,
    label: 'Deprecated',
    className: 'text-yellow-500',
    bannerClassName: 'text-yellow-500 bg-yellow-50',
  },
  unavailable: {
    icon: XCircle,
    label: 'Unavailable',
    className: 'text-red-500',
    bannerClassName: 'text-red-500 bg-red-50',
  },
  pending: {
    icon: Clock,
    label: 'Pending',
    className: 'text-gray-500',
    bannerClassName: 'text-gray-500 bg-gray-50',
  },
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get mining job status configuration with fallback
 */
export function getMiningJobStatusConfig(status: string): StatusConfig {
  return MINING_JOB_STATUS_CONFIG[status as MiningJobStatus] || MINING_JOB_STATUS_CONFIG.pending;
}

/**
 * Get MCP server status configuration with fallback
 */
export function getMcpServerStatusConfig(status: string): FullStatusConfig {
  return MCP_SERVER_STATUS_CONFIG[status as McpServerStatus] || MCP_SERVER_STATUS_CONFIG.pending;
}

/**
 * Check if a mining job status indicates active processing
 */
export function isMiningJobActive(status: string): boolean {
  return ['pending', 'running', 'scanning', 'extracting', 'classifying', 'chunking', 'generating', 'review'].includes(status);
}

/**
 * Check if a mining job status shows a spinning icon
 */
export function isMiningJobSpinning(status: string): boolean {
  return ['running', 'scanning', 'extracting', 'classifying', 'chunking', 'generating'].includes(status);
}
